import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getMalus, deciderMalus, annoncerMalus, exporterMalus } from '../services/api';

/**
 * Malus — ce que le terrain doit, et depuis quand
 *
 * Rien n'est saisi ici. Les trois manquements se lisent dans l'historique des
 * étapes, que le webhook Zoho horodate : un compte rendu refusé par Covage et
 * repris trop tard, une clôture en retard, un rendez-vous non honoré. La page
 * montre donc des faits datés, avec la main qui a posé l'étape — c'est ce qui
 * permet de les discuter sans se fâcher.
 *
 * Un malus compte dès qu'il a été annoncé — ne jamais répondre ne doit pas
 * suffire à l'éviter. Le technicien reçoit le détail sur WhatsApp ; sa réponse
 * est enregistrée telle quelle, et s'il conteste, la ligne passe en rouge et
 * attend une décision. Seule la conduite d'activité peut alors la retirer, en
 * disant pourquoi. Ce qui n'a pas été annoncé ne compte pas : on ne retient pas
 * à quelqu'un ce qu'il ignore.
 */

const EUR = n => (n || 0).toLocaleString('fr-FR') + ' €';

const horodate = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return String(iso).slice(0, 16).replace('T', ' ');
  return d.toLocaleString('fr-FR', {
    timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

const moisLisible = m => {
  if (!m) return '';
  const [a, mm] = m.split('-');
  const noms = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet',
    'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  return noms[Number(mm) - 1] + ' ' + a;
};

const ETATS = {
  candidat: { texte: 'à déclarer',  couleur: 'var(--amber)' },
  propose:  { texte: 'proposé',     couleur: 'var(--text3)' },
  notifie:  { texte: 'annoncé',     couleur: 'var(--blue)' },
  confirme: { texte: 'confirmé',    couleur: 'var(--green)' },
  conteste: { texte: 'contesté',    couleur: 'var(--accent)' },
  annule:   { texte: 'annulé',      couleur: 'var(--text3)' },
};

const TYPES = {
  cr_non_conforme: 'CR non conforme',
  cri_non_cloture: 'CRI non clôturé',
  rdvnh:           'RDVNH',
};

/* ─── Petits éléments ────────────────────────────────────────────────────── */

function Chiffre({ valeur, legende, couleur, titre }) {
  return (
    <div title={titre} style={{ minWidth: 120 }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: couleur || 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
        {valeur}
      </div>
      <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .3 }}>
        {legende}
      </div>
    </div>
  );
}

function Bouton({ children, onClick, couleur = 'var(--blue)', plein, titre, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} title={titre} style={{
      padding: '4px 9px', fontSize: 11, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
      background: plein ? couleur : 'transparent',
      color: plein ? '#fff' : couleur,
      border: '1px solid ' + (plein ? couleur : 'var(--border2)'),
      borderRadius: 5, opacity: disabled ? .45 : 1, whiteSpace: 'nowrap',
    }}>{children}</button>
  );
}

/* ─── Une ligne ──────────────────────────────────────────────────────────── */

function Ligne({ l, surAction, occupe }) {
  const [ouvert, setOuvert] = useState(false);
  const etat = ETATS[l.statut] || ETATS.propose;
  const cellule = { padding: '7px 8px', fontSize: 12, color: 'var(--text)', verticalAlign: 'top' };
  const muet = !l.montant || l.statut === 'annule';

  return (
    <>
      <tr style={{
        borderBottom: '1px solid var(--border)', opacity: muet ? .55 : 1,
        // Une contestation appelle une décision : elle ne doit pas se noyer
        // dans la liste.
        background: l.statut === 'conteste' ? 'rgba(239,68,68,0.08)' : undefined,
      }}>
        <td style={cellule}>
          <div style={{ fontWeight: 600 }}>{TYPES[l.type] || l.type}</div>
          <div style={{ fontSize: 10, color: 'var(--text3)' }}>{l.compte.replace('VALEA-', '')}</div>
        </td>
        <td style={cellule}>
          {l.ref_commande
            ? <a href={'/dossier/' + l.dossier_id} target="_blank" rel="noreferrer"
                style={{ color: 'var(--blue)', textDecoration: 'none', fontWeight: 600 }}>{l.ref_commande}</a>
            : <span style={{ color: 'var(--text3)' }}>commande inconnue</span>}
          <div style={{ fontSize: 10, color: 'var(--text3)' }}>{l.client}{l.ville ? ' — ' + l.ville : ''}</div>
        </td>
        <td style={cellule}>
          <div>{horodate(l.entree)}</div>
          <div style={{ fontSize: 10, color: 'var(--text3)' }}>
            {l.en_cours ? 'toujours en cours' : '→ ' + horodate(l.sortie)}
          </div>
        </td>
        <td style={{ ...cellule, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          <div>{l.heures_ouvrees} h ouvrées</div>
          {l.echeance && (
            <div style={{ fontSize: 10, color: 'var(--text3)' }}>limite {horodate(l.echeance)}</div>
          )}
        </td>
        <td style={{ ...cellule, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: l.montant ? 'var(--text)' : 'var(--text3)' }}>
            {l.montant ? EUR(l.montant) : '—'}
          </span>
        </td>
        <td style={cellule}>
          <span style={{
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .3,
            color: etat.couleur,
          }}>{etat.texte}</span>
          {l.motif_zero && (
            <div style={{ fontSize: 10, color: 'var(--text3)', maxWidth: 220 }}>{l.motif_zero}</div>
          )}
          {l.reponse && (
            <div style={{ fontSize: 10, color: 'var(--text2)', fontStyle: 'italic', maxWidth: 220 }}>
              « {l.reponse} »
            </div>
          )}
        </td>
        <td style={{ ...cellule, textAlign: 'right' }}>
          <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {l.type === 'rdvnh' && !l.declare && (
              <Bouton couleur="var(--amber)" plein disabled={occupe}
                titre="Déclarer le rendez-vous réellement non honoré : 150 € dus, et le malus de clôture de la même commande est annulé."
                onClick={() => surAction(l, 'declarer')}>déclarer</Bouton>
            )}
            {l.montant > 0 && !l.notifie_le && (
              <Bouton plein disabled={occupe}
                titre="Envoyer le détail au technicien sur WhatsApp et attendre sa réponse."
                onClick={() => surAction(l, 'annoncer')}>annoncer</Bouton>
            )}
            {l.montant > 0 && l.statut !== 'annule' && (
              <Bouton couleur={l.statut === 'conteste' ? 'var(--accent)' : 'var(--text3)'}
                plein={l.statut === 'conteste'} disabled={occupe}
                titre="Ne plus retenir ce malus. La ligne reste visible, avec le motif du retrait."
                onClick={() => surAction(l, 'annuler')}>retirer</Bouton>
            )}
            <Bouton couleur="var(--text3)" onClick={() => setOuvert(o => !o)}>
              {ouvert ? 'moins' : 'détail'}
            </Bouton>
          </div>
        </td>
      </tr>
      {ouvert && (
        <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg2)' }}>
          <td colSpan={7} style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text2)' }}>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div><strong>Étape posée par</strong><br />{l.pose_par || 'inconnu'}</div>
              <div><strong>Étape suivante</strong><br />{l.etape_suivante || 'aucune — toujours en cours'}</div>
              <div><strong>Work order</strong><br />{l.ref_work_order || '—'}</div>
              <div><strong>Annoncé le</strong><br />{horodate(l.notifie_le)}</div>
              <div><strong>Confirmé le</strong><br />{horodate(l.confirme_le)}</div>
              <div><strong>Identifiant</strong><br /><code style={{ fontSize: 10 }}>{l.id}</code></div>
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {l.montant > 0 && l.statut !== 'confirme' && (
                <Bouton couleur="var(--green)" disabled={occupe}
                  titre="Le technicien a répondu autrement que par WhatsApp — de vive voix, par courriel."
                  onClick={() => surAction(l, 'confirmer')}>enregistrer son accord</Bouton>
              )}
              {l.montant > 0 && l.statut !== 'conteste' && (
                <Bouton couleur="var(--accent)" disabled={occupe}
                  onClick={() => surAction(l, 'contester')}>il le conteste</Bouton>
              )}
              {['confirme', 'conteste', 'annule'].includes(l.statut) && (
                <Bouton couleur="var(--text3)" disabled={occupe}
                  titre="Revenir à l’état proposé, sans effacer l’historique."
                  onClick={() => surAction(l, 'rouvrir')}>rouvrir</Bouton>
              )}
              {l.type === 'rdvnh' && l.declare && (
                <Bouton couleur="var(--text3)" disabled={occupe}
                  onClick={() => surAction(l, 'retirer_declaration')}>retirer la déclaration</Bouton>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ─── La page ────────────────────────────────────────────────────────────── */

export default function Malus() {
  const [mois, setMois] = useState('');
  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [filtreCompte, setFiltreCompte] = useState('');
  const [filtreType, setFiltreType] = useState('');
  const [cacherZero, setCacherZero] = useState(true);
  const [message, setMessage] = useState('');

  const charger = useCallback(async (m) => {
    setChargement(true); setErreur('');
    try {
      const r = await getMalus(m || undefined);
      setData(r);
      if (!m && r.mois_disponibles && r.mois_disponibles.length) setMois(r.mois_disponibles[0]);
    } catch (e) {
      setErreur(e.response?.data?.error || e.message);
    } finally { setChargement(false); }
  }, []);

  useEffect(() => { charger(mois); }, [mois, charger]);

  const surAction = async (l, action) => {
    let motif = '';
    if (action === 'annuler' || action === 'contester') {
      motif = window.prompt(action === 'annuler'
        ? 'Pourquoi ce malus n’est-il pas retenu ?'
        : 'Que conteste le technicien ?') || '';
      if (!motif.trim()) return;
    }
    setOccupe(true); setMessage('');
    try {
      if (action === 'annoncer') {
        const r = await annoncerMalus(l.id);
        setMessage('Message envoyé au ' + r.telephone + ' — sa réponse arrivera ici.');
      } else {
        await deciderMalus(l.id, action, motif);
      }
      await charger(mois);
    } catch (e) {
      setMessage('Échec : ' + (e.response?.data?.error || e.message));
    } finally { setOccupe(false); }
  };

  const lignes = useMemo(() => {
    let l = (data && data.lignes) || [];
    if (filtreCompte) l = l.filter(x => x.compte === filtreCompte);
    if (filtreType) l = l.filter(x => x.type === filtreType);
    if (cacherZero) l = l.filter(x => x.montant > 0);
    return l;
  }, [data, filtreCompte, filtreType, cacherZero]);

  const comptes = useMemo(() => {
    const s = new Set(((data && data.lignes) || []).map(l => l.compte).filter(Boolean));
    return [...s].sort();
  }, [data]);

  const t = (data && data.totaux) || { retenu: 0, a_annoncer: 0, conteste: 0, annule: 0 };

  const exporter = async () => {
    try {
      const r = await exporterMalus({ mois: mois || undefined, technicien: filtreCompte || undefined });
      const dispo = r.headers['content-disposition'] || '';
      const trouve = /filename="([^"]+)"/.exec(dispo);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = trouve ? trouve[1] : 'malus.xlsx';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      setMessage('Export impossible : ' + (e.response?.data?.error || e.message));
    }
  };

  const champ = {
    padding: '6px 10px', fontSize: 12, borderRadius: 6,
    border: '1px solid var(--border2)', background: 'var(--bg)', color: 'var(--text)',
  };

  return (
    <div style={{
      padding: 20, maxWidth: 1500, margin: '0 auto',
      // Sans ces trois lignes la page prend sa largeur naturelle et déborde du
      // panneau : sur un portable, le bouton d'export passait sous le bord.
      width: '100%', minWidth: 0, boxSizing: 'border-box',
    }}>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Malus</h1>
        <select value={mois} onChange={e => setMois(e.target.value)} style={champ}>
          <option value="">tous les mois</option>
          {((data && data.mois_disponibles) || []).map(m =>
            <option key={m} value={m}>{moisLisible(m)}</option>)}
        </select>
        <select value={filtreCompte} onChange={e => setFiltreCompte(e.target.value)} style={champ}>
          <option value="">tous les comptes</option>
          {comptes.map(c => <option key={c} value={c}>{c.replace('VALEA-', '')}</option>)}
        </select>
        <select value={filtreType} onChange={e => setFiltreType(e.target.value)} style={champ}>
          <option value="">tous les motifs</option>
          {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <label style={{ fontSize: 12, color: 'var(--text2)', display: 'flex', alignItems: 'center', gap: 5 }}>
          <input type="checkbox" checked={cacherZero} onChange={e => setCacherZero(e.target.checked)} />
          ne montrer que ce qui coûte
        </label>
        <div style={{ flex: 1 }} />
        <Bouton onClick={exporter} titre="Le détail affiché, en classeur Excel filtrable.">
          exporter en Excel
        </Bouton>
      </div>

      {message && (
        <div style={{
          padding: '8px 12px', marginBottom: 12, borderRadius: 6, fontSize: 12,
          background: 'var(--bg2)', color: 'var(--text2)', border: '1px solid var(--border2)',
        }}>{message}</div>
      )}

      {erreur && (
        <div style={{
          padding: '10px 12px', marginBottom: 12, borderRadius: 6, fontSize: 13,
          background: 'var(--accent-bg, #fee)', color: 'var(--accent)',
        }}>{erreur}</div>
      )}

      {/* Le compte, en quatre chiffres */}
      <div style={{
        display: 'flex', gap: 28, flexWrap: 'wrap', padding: '14px 16px', marginBottom: 14,
        background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8,
      }}>
        <Chiffre valeur={EUR(t.retenu)} legende="retenus" couleur="var(--green)"
          titre="Malus annoncés au technicien et non annulés. C'est ce qui se déduit de l'attachement." />
        <Chiffre valeur={EUR(t.a_annoncer)} legende="reste à annoncer" couleur="var(--blue)"
          titre="Calculés mais pas encore annoncés : ils ne comptent pas tant que le technicien ne les connaît pas." />
        <Chiffre valeur={EUR(t.conteste)} legende="dont contestés" couleur="var(--accent)"
          titre="Le technicien n'est pas d'accord. Ils restent comptés tant que vous ne les retirez pas." />
        <Chiffre valeur={EUR(t.annule)} legende="retirés" couleur="var(--text3)"
          titre="Écartés par la conduite d'activité, avec leur motif. Ils ne se déduisent pas." />
        <Chiffre valeur={lignes.length} legende="lignes affichées" />
      </div>

      {chargement && <div style={{ color: 'var(--text3)', fontSize: 13 }}>Lecture de l’historique des étapes…</div>}

      {!chargement && !lignes.length && (
        <div style={{ color: 'var(--text3)', fontSize: 13, padding: 20, textAlign: 'center' }}>
          Aucun malus {cacherZero ? 'à ce mois-ci' : 'du tout'} avec ces filtres.
        </div>
      )}

      {!chargement && lignes.length > 0 && (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 880 }}>
            <thead>
              <tr style={{ background: 'var(--bg2)' }}>
                {['Motif / compte', 'Commande', 'Entrée → sortie', 'Délai', 'Montant', 'État', ''].map((h, i) => (
                  <th key={i} style={{
                    padding: '7px 8px', fontSize: 10, fontWeight: 700, color: 'var(--text3)',
                    textTransform: 'uppercase', letterSpacing: .3,
                    textAlign: i === 4 || i === 6 ? 'right' : 'left',
                    borderBottom: '1px solid var(--border)',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lignes.map(l => (
                <Ligne key={l.id} l={l} surAction={surAction} occupe={occupe} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── Ce que ces chiffres veulent dire ──────────────────────────── */}
      <div style={{
        marginTop: 18, padding: '14px 16px', background: 'var(--bg2)',
        border: '1px solid var(--border)', borderRadius: 8, fontSize: 12,
        color: 'var(--text2)', lineHeight: 1.65,
      }}>
        <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
          À savoir sur ces chiffres
        </div>
        <div>
          <strong>Rien n’est saisi à la main.</strong> Les trois manquements se lisent dans
          l’historique des étapes, horodaté par le webhook Zoho. La colonne « détail » dit
          qui a posé l’étape et vers quoi le dossier est reparti.
        </div>
        <div style={{ marginTop: 6 }}>
          <strong>Le mois est celui de l’intervention</strong>, pas celui où le délai a expiré :
          le malus se déduit de l’attachement qui paie cette intervention.
        </div>
        <div style={{ marginTop: 6 }}>
          <strong>CR non conforme — 50 €.</strong> Le technicien a 48 heures ouvrées pour
          reprendre un compte rendu refusé. Le délai se compte en heures murales : un samedi,
          un dimanche ou un férié ne comptent pour rien, un jour ouvré compte ses vingt-quatre
          heures. Refusé un vendredi à 15 h, c’est jusqu’au mardi 15 h. Le forfait n’est dû
          qu’une fois par commande, même si elle est refusée trois fois.
        </div>
        <div style={{ marginTop: 6 }}>
          <strong>CRI non clôturé — 15 € par jour ouvré</strong> entamé tant que l’étape dure.
        </div>
        <div style={{ marginTop: 6 }}>
          <strong>RDVNH — 150 €, sur déclaration seulement.</strong> L’étape dit qu’un doute
          existe, pas qu’il est fondé. Tant que personne ne l’a déclaré, la ligne reste à
          zéro. Une fois déclaré, il annule le malus de clôture de la même commande : on ne
          paie pas deux fois le même matin perdu.
        </div>
        <div style={{ marginTop: 6 }}>
          <strong>Un malus compte dès qu’il est annoncé.</strong> Le technicien reçoit le
          détail sur WhatsApp ; « oui », « ok », un pouce levé valent accord, « non » vaut
          contestation, et tout ce qui ne se lit pas clairement est recopié tel quel sans rien
          décider. Ne pas répondre ne l’annule pas — mais une contestation passe la ligne en
          rouge, et c’est à vous de la retirer ou de la maintenir. Ce qui n’a pas encore été
          annoncé ne compte pas.
        </div>
      </div>
    </div>
  );
}
